// Results: the rank and its ladder first, then Share (which sends your challenge on the edition),
// how your challenges went, where you placed, a per-word scorecard, and when the next edition comes
// with at most one ask under it. Once an edition's day is over, its solver's line sits beside the
// #1 player's; a past edition you didn't finish shows just those (and a way to play it). Results
// that open as a game ends land the score (it counts up, the ladder fills, the rank climbs and
// stamps), then reveal any achievements the game earned; on a day with none, they may bring the
// rating ask instead.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router'
import type { BadgeCategory, Challenge, EditionInfo, FreshAchievements, Me, MyAchievements, Play, RankStep, ShareRequest } from '@wbwl/contract'
import { badgeChanged } from '../components/achievements-ui.tsx'
import { Badge } from '../components/Badge.tsx'
import { AchievementReveal } from '../components/Reveal.tsx'
import { scorePlay } from '@wbwl/rules'
import { Histogram, Ladder, WordTiles } from '../components/game-ui.tsx'
import { Icon } from '../components/Icon.tsx'
import { RatingAsk } from '../components/RatingAsk.tsx'
import { InstallButton, KeepAsk } from '../components/reminder-ui.tsx'
import { DayCard } from '../components/rivals-ui.tsx'
import { ShareSheet } from '../components/share-ui.tsx'
import { Avatar, Countdown, ErrorNote, Loading, NotFound, useToast } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useEdition, useEditionChallenges, useLeaderboard, useMe, usePlay, useThread } from '../lib/data.ts'
import { fmt, shortDate, weekday } from '../lib/game.ts'
import { useIsDesktop } from '../lib/layout.ts'
import { dayOf, type Day } from '../lib/rivals.ts'
import { newShareCode } from '../lib/share.ts'
import { invalidate, setResource } from '../lib/store.ts'
import { playCountTick, playRankClack, playStamp, preloadAchievementSounds, preloadLandingSounds } from '../lib/sound.ts'

export default function ResultsPage() {
  const n = Number(useParams().number)
  if (!Number.isInteger(n) || n < 0) return <NotFound />
  return <ResultsLoader n={n} />
}

function ResultsLoader({ n }: { n: number }) {
  const ed = useEdition(n)
  const pq = usePlay(ed.data?.number ?? null)
  const me = useMe().data
  if (ed.error) return <div className="page"><ErrorNote error={ed.error} retry={() => ed.reload()} /></div>
  if (pq.error) return <div className="page"><ErrorNote error={pq.error} retry={() => pq.reload()} /></div>
  if (!ed.data || !pq.data) return <div className="page"><Loading label="Counting your points…" /></div>
  if (pq.data.phase !== 'done') return ed.data.isToday ? <Navigate to="/play" replace /> : <Recap info={ed.data} play={pq.data} me={me} />
  return <Results key={ed.data.number} info={ed.data} play={pq.data} me={me} />
}

function Results({ info, play, me }: { info: EditionInfo; play: Play; me: Me | undefined }) {
  const board = useLeaderboard(play.ranked ? info.number : null, 'today').data
  const challenges = useEditionChallenges(info.number).data?.challenges
  // The thread's count badges Discussion, and loading it here has the thread ready when it's opened.
  const comments = useThread(info.number).data?.count ?? 0
  const [share, setShare] = useState(false)
  const [shareCh, setShareCh] = useState<Challenge | null>(null)
  const toast = useToast()
  const navigate = useNavigate()
  const { pathname, search, hash, state } = useLocation()
  // The game sends you here marked finished, and the mark comes off at once: Back to these results doesn't ask again.
  const [finished] = useState(() => (state as { finished?: boolean } | null)?.finished === true)
  useEffect(() => {
    if (finished) navigate({ pathname, search, hash }, { replace: true })
  }, [])
  const { land, shown, rankName } = useScoreLanding(finished, play.scores.total, info.ranks, play.standing.name)
  const achievements = useFreshAchievements(finished)
  // The reveal rises 1.6 s after the score lands, so it has time to sink in; nothing earned, no sheet.
  // While the share sheet is up it waits, and rises 1.6 s after that closes.
  const [reveal, setReveal] = useState<'wait' | 'open' | 'closed'>('wait')
  useEffect(() => {
    if (land !== 'done' || !achievements || reveal !== 'wait' || share) return
    if (!achievements.unlocks.length) { setReveal('closed'); return }
    const t = setTimeout(() => {
      setReveal('open')
      api.post('/me/achievements/seen', { ids: achievements.unlocks.map((u) => u.id) }).catch(() => {})
    }, 1600)
    return () => clearTimeout(t)
  }, [land, achievements, reveal, share])

  async function wear(category: BadgeCategory) {
    try {
      badgeChanged(await api.put<MyAchievements>('/me/badge', { category }))
      toast(<><Icon name="check" />It shows beside your name now</>)
    } catch (e) {
      toast(e instanceof ApiFailure && e.body.message ? e.body.message : 'Couldn’t change your badge. Try again.')
    }
  }
  // One share link per visit, however often the sheet opens; it counts once something is sent.
  const [code] = useState(newShareCode)
  // Everyone you're up against on this edition, from every challenge you're in, as one card.
  const day = challenges?.length ? dayOf({ edition: info.number, date: info.date, challenges }) : null

  // Share sends your challenge on this edition: the API makes it, or hands back the one you have.
  // The share link is registered with it at once, so it opens the challenge whatever goes out.
  async function openShare() {
    setShare(true)
    if (shareCh) return
    try {
      const ch = await api.post<Challenge>('/challenges', { edition: info.number })
      setResource(`challenge:${ch.code}`, ch)
      invalidate(`edchallenges:${info.number}`, 'mychallenges')
      setShareCh(ch)
      api.post('/shares', { code, edition: info.number, challenge: ch.code } satisfies ShareRequest).catch(() => {})
    } catch (e) {
      setShare(false)
      toast(e instanceof ApiFailure && e.body.message ? e.body.message : 'Couldn’t get your link ready. Try again.')
    }
  }

  async function accept(d: Day) {
    // An invite that came after you finished: joining it opens the boards.
    await Promise.all(d.toAccept.map((c) => api.post(`/challenges/${c.code}/join`).catch(() => {})))
    invalidate(`edchallenges:${info.number}`, 'mychallenges', 'rivals')
    navigate(`/c/${(d.open ?? d.toAccept[0]).code}`)
  }

  const phx = play.scores.phoenix
  const guest = me?.kind === 'guest'

  return (
    <div className="page results" data-land={land}>
      <div className="topbar">
        <Link className="ibtn" to="/" aria-label="Close"><Icon name="close" /></Link>
        <div className="title"><b>No. {info.number}</b><small>FINAL · {shortDate(info.date)}{play.ranked ? '' : ' · ARCHIVE'}</small></div>
      </div>

      <section className="res-hero">
        <div className="double-rule" />
        <div className="eyebrow dateline center">{!play.ranked ? 'Archive edition' : info.isToday ? 'Today’s edition' : `${weekday(info.date)}’s edition`} · you made the</div>
        <div className="rule ink" />
        <div key={land === 'count' ? rankName : 'final'} className={`h-serif res-rank${land === 'count' ? ' tick' : ''}`}>{rankName}</div>
        <div className="res-total">{fmt(shown)}</div>
        <div className="faint mono small res-split">{fmt(play.scores.rowsTotal)} from {play.rows.length} words · {phx ? `+${phx.total} phoenix` : play.phoenixResult === 'no_word' ? 'no phoenix today' : play.phoenixResult === 'day_over' ? 'the day ended first' : 'phoenix skipped'}</div>
      </section>

      <section className="res-ladder">
        <Ladder total={shown} ranks={info.ranks} width="100%" />
        <div className="ladder-note faint mono small center">{play.standing.next ? <><b className="ink">{play.standing.toNext}</b> to {play.standing.next.name} · </> : null}solver’s best {fmt(info.best)}</div>
      </section>

      <section className="res-next land-later">
        <button type="button" className="btn gold" onClick={() => void openShare()}><Icon name="share" />Share</button>
      </section>

      {finished && reveal === 'closed' && achievements && <AchievementStrip fresh={achievements} />}

      {/* A challenge nobody has joined yet is just you: a line, not a card. */}
      {day && (day.players.length > 1
        ? <div className="res-challenges"><DayCard day={day} title={`Your challenges · No. ${info.number}`} onAccept={() => accept(day)} /></div>
        : <div className="res-next-note faint small center">Nobody has joined your challenge yet.</div>)}

      {play.placement && (
        <section className="card res-place land-later">
          <div className="place-head"><span className="place-top">Top {play.placement.topPercent}%</span><span className="muted">#{fmt(play.placement.position)} of {fmt(play.placement.players)} player{play.placement.players === 1 ? '' : 's'}{info.isToday ? ' today' : ''}</span></div>
          {board && <Histogram bins={board.histogram} you={play.scores.total} height={62} />}
        </section>
      )}

      <section className="res-words">
        <div className="sec-head"><h3>Your words</h3><span className="eyebrow">pts × mult</span></div>
        {play.rows.map((w, r) => (
          <div className="sc-row" key={r}><span className="n">{r + 1}</span><WordTiles edition={info} word={w} row={r} /><span className="m">{play.scores.rows[r].pts}×{play.scores.rows[r].mult}</span><span className="s">{play.scores.rows[r].total}</span></div>
        ))}
        <div className={`sc-row${phx ? ' phx' : ''}`}>
          <span className="n burn-ink"><Icon name="feather" size={16} /></span>
          {play.phoenix ? <WordTiles edition={info} word={play.phoenix} k="ash" /> : <span className="faint mono small">{play.phoenixResult === 'no_word' ? 'Your ashes couldn’t spell a word' : play.phoenixResult === 'day_over' ? 'The day ended before the phoenix' : 'Skipped'}</span>}
          <span className="m">{phx ? `${phx.pts}×${phx.mult}` : ''}</span><span className="s">{phx?.total ?? 0}</span>
        </div>
        <div className="total-row"><span className="eyebrow ink">Total</span><span className="total-num">{fmt(play.scores.total)}</span></div>
      </section>

      {info.bestLine
        ? <BestLines info={info} me={me} />
        : <p className="faint small center recap-spoiler">Finish it to see the best words: the solver’s line and the day’s #1.</p>}

      <section className="res-actions">
        <Link className="btn ghost" to={`/leaderboard?n=${info.number}`}><Icon name="trophy" />Leaderboard</Link>
        <Link className="btn ghost" to={`/discussion/${info.number}`} aria-label={comments ? `Discussion, ${comments} comment${comments === 1 ? '' : 's'}` : undefined}>
          <span className="ico-count"><Icon name="message" />{comments > 0 && <b aria-hidden="true">{comments > 99 ? '99+' : comments}</b>}</span>Discussion
        </Link>
      </section>

      {/* Today's countdown, and one ask: Sign in for a guest (on any edition), the reminder for everyone else. */}
      {(info.isToday || guest) && (
        <section className="card next-ed">
          {info.isToday && (
            <div className="next-top">
              <Icon name="clock" size={22} />
              <div><span className="eyebrow">Next edition in</span><b className="mono countdown"><Countdown to={info.nextAt} /></b></div>
              <span className="muted small">The solver’s best words and the day’s #1 go up at midnight Pacific.</span>
            </div>
          )}
          {guest ? <SignInAsk /> : <KeepAsk />}
        </section>
      )}

      {me && <ShareSheet open={share} onClose={() => setShare(false)} info={info} play={play} me={me} challenge={shareCh} onChange={setShareCh} code={code} />}
      {/* The rating ask waits its turn: after the score lands, and after the reveal when the game earned something. */}
      <RatingAsk mode="daily" edition={info.number} finished={finished && land === 'done' && achievements !== null && (achievements.unlocks.length === 0 || reveal === 'closed')} held={share || reveal === 'open'} />
      {reveal === 'open' && achievements && (
        <AchievementReveal unlocks={achievements.unlocks} claimed={achievements.claimed} onClose={() => setReveal('closed')}
          onSignIn={() => navigate('/signin')} onWear={(c) => void wear(c)} onSeeAll={() => navigate('/you/achievements')} />
      )}

    </div>
  )
}

/** A guest's ask, in place of the reminder's: Sign in, and Add to home screen where the browser offers it. */
function SignInAsk() {
  return (
    <div className="next-ask">
      <Link to="/signin" className="ask-say signin">
        <Icon name="lock" size={20} />
        <div><b>Sign in to keep your streak</b><span className="small">Your games follow you to every device, and your name goes on the leaderboard.</span></div>
        <Icon name="next" size={18} />
      </Link>
      <InstallButton />
    </div>
  )
}

/** A past edition you didn't finish: how it went for everyone, the best lines, and a way to play it. */
function Recap({ info, play, me }: { info: EditionInfo; play: Play; me: Me | undefined }) {
  const started = play.rows.length > 0
  return (
    <div className="page results">
      <div className="topbar">
        <Link className="ibtn" to="/" aria-label="Close"><Icon name="close" /></Link>
        <div className="title"><b>No. {info.number}</b><small>FINAL · {shortDate(info.date)}</small></div>
      </div>

      <section className="res-hero">
        <div className="double-rule" />
        <div className="eyebrow dateline center">{weekday(info.date)}’s edition · final</div>
        <div className="rule ink" />
        <div className="h-serif recap-head">{started ? 'You didn’t finish this one' : 'You missed this one'}</div>
        <div className="faint mono small">
          {info.players ? <>{fmt(info.players)} player{info.players === 1 ? '' : 's'} finished it on the day{info.average !== null && <> · average {fmt(info.average)}</>}</> : 'Nobody finished it on the day'}
        </div>
      </section>

      <section className="recap-play">
        <Link className="btn ink block" to={`/play/${info.number}`}>{started ? `Finish No. ${info.number}` : `Play No. ${info.number}`} from the archive</Link>
        <span className="faint small">Archive games don’t count toward your stats or the leaderboard.</span>
      </section>

      {info.bestLine && <BestLines info={info} me={me} />}

      <section className="res-actions">
        <Link className="btn ghost span2" to={`/leaderboard?n=${info.number}`}><Icon name="trophy" />Leaderboard</Link>
      </section>
    </div>
  )
}

type Line = NonNullable<EditionInfo['bestLine']>

/** The solver's line beside the leaderboard's #1, row by row, once the edition's day is over. */
function BestLines({ info, me }: { info: EditionInfo; me: Me | undefined }) {
  const desktop = useIsDesktop()
  const { hash } = useLocation()
  const section = useRef<HTMLElement>(null)
  // Yesterday's half of the Today card opens the results here. The scroll waits a frame, past the
  // app's scroll to the top of a new page, and holds while cards above it load (for up to 3 s),
  // until the reader scrolls.
  useEffect(() => {
    const el = section.current
    if (hash !== '#best' || !el) return
    const go = () => el.scrollIntoView({ block: 'start' })
    const frame = requestAnimationFrame(go)
    const grown = new ResizeObserver(go)
    grown.observe(document.body)
    const stop = () => grown.disconnect()
    const timer = setTimeout(stop, 3000)
    const reader = ['wheel', 'touchstart', 'keydown'] as const
    for (const e of reader) addEventListener(e, stop, { passive: true, once: true })
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
      stop()
      for (const e of reader) removeEventListener(e, stop)
    }
  }, [hash])
  const solver = info.bestLine!
  const top = info.topLine
  // Two lines side by side take smaller tiles; the solver's alone gets Your words' size.
  const tile = !top ? 26 : desktop ? 24 : 19
  const lines: Line[] = top ? [solver, top] : [solver]
  const scores = lines.map((l) => scorePlay(info, l))
  const rows = Math.max(...lines.map((l) => l.rows.length))
  const you = top && top.playerId === me?.id
  const name = you ? 'You' : top?.displayName

  /** One row's cells, a word and its score from each line; the higher score of the two is marked. */
  function cells(words: readonly (string | null | undefined)[], totals: readonly (number | null)[], row?: number) {
    const high = totals.length > 1 && totals[0] !== totals[1] ? Math.max(...totals.map((t) => t ?? 0)) : null
    return words.map((w, i) => (
      <div className="cmp-cell" key={i}>
        {w ? <WordTiles edition={info} word={w} row={row} size={tile} gap={2} k={row === undefined ? 'ash' : 'played'} /> : <span className="faint mono small">—</span>}
        <span className={`s${high !== null && totals[i] === high ? ' up' : ''}`}>{totals[i] ?? ''}</span>
      </div>
    ))
  }

  let note: string
  if (!top) note = `Nobody finished No. ${info.number} on its day, so there’s no #1 to compare.`
  else if (top.total > solver.total) note = `${name} beat the solver by ${fmt(top.total - solver.total)}.`
  else if (top.total === solver.total) note = `${name} matched the solver’s line.`
  else note = `${name} reached ${Math.floor((top.total / solver.total) * 100)}% of the solver’s line.`

  return (
    <section className="res-best" id="best" ref={section}>
      <div className="sec-head"><h3>Best words</h3><span className="eyebrow">No. {info.number}</span></div>
      <div className={`cmp${top ? '' : ' solo'}`}>
        <div className="cmp-row cmp-head">
          <span />
          <span className="cmp-who"><Icon name="sparkle" size={16} />Solver</span>
          {top && <span className="cmp-who"><Avatar id={top.playerId} name={top.displayName} src={top.avatar} size={20} /><span className="ellipsis">#1 · {name}</span></span>}
        </div>
        {Array.from({ length: rows }, (_, r) => (
          <div className="cmp-row" key={r}><span className="n">{r + 1}</span>{cells(lines.map((l) => l.rows[r]), scores.map((s) => s.rows[r]?.total ?? null), r)}</div>
        ))}
        <div className="cmp-row phx">
          <span className="n burn-ink"><Icon name="feather" size={14} /></span>
          {cells(lines.map((l) => l.phoenix), scores.map((s) => s.phoenix?.total ?? null))}
        </div>
        <div className="cmp-row cmp-total">
          <span className="eyebrow ink">Total</span>
          {lines.map((l, i) => <span className="total-num" key={i}>{fmt(l.total)}</span>)}
        </div>
      </div>
      <p className="faint small cmp-note">{note}</p>
    </section>
  )
}

type Land = 'count' | 'landed' | 'done'

/**
 * The score lands as a game ends: after a breath it counts up for 1.2 s (ticking as it goes), the
 * ladder fills behind it and the rank name climbs each step it passes (with a clack), then the
 * rank stamps down. Revisits and reduced motion show the final score at once.
 */
function useScoreLanding(finished: boolean, total: number, ranks: readonly RankStep[], finalName: string) {
  const animate = useMemo(() => finished && !matchMedia('(prefers-reduced-motion: reduce)').matches, [])
  const [land, setLand] = useState<Land>(animate ? 'count' : 'done')
  const [shown, setShown] = useState(animate ? 0 : total)
  const nameAt = (v: number) => ranks.reduce((name, r) => (v >= r.at ? r.name : name), ranks[0]?.name ?? finalName)

  useEffect(() => {
    if (!animate) return
    // The stamp comes at the end; loading it now means it's there in time.
    preloadLandingSounds()
    const COUNT = 1200
    const DELAY = 250
    let frame = 0
    let start = 0
    let lastTick = -Infinity
    let lastName = nameAt(0)
    const timers: ReturnType<typeof setTimeout>[] = []
    const step = (now: number) => {
      if (!start) start = now
      const p = Math.min(1, (now - start) / COUNT)
      const v = Math.round(total * (1 - (1 - p) ** 3))
      setShown(v)
      if (now - lastTick >= 70 && p < 1) { lastTick = now; playCountTick(p) }
      const name = nameAt(v)
      if (name !== lastName) { lastName = name; if (p < 1) playRankClack() }
      if (p < 1) { frame = requestAnimationFrame(step); return }
      setLand('landed')
      playStamp()
      timers.push(setTimeout(() => setLand('done'), 450))
    }
    timers.push(setTimeout(() => { frame = requestAnimationFrame(step) }, DELAY))
    return () => { cancelAnimationFrame(frame); timers.forEach(clearTimeout) }
  }, [])

  const rankName = land === 'count' ? nameAt(shown) : finalName
  return { land, shown, rankName }
}

/** What the game just earned (and what's in reach), fetched once as a game ends; the reveal's sounds load meanwhile. */
function useFreshAchievements(finished: boolean) {
  const [fresh, setFresh] = useState<FreshAchievements | null>(null)
  useEffect(() => {
    if (!finished) return
    let live = true
    api.get<FreshAchievements>('/me/achievements/fresh')
      .then((f) => {
        if (!live) return
        if (f.unlocks.length) preloadAchievementSounds({ ablaze: f.unlocks.some((u) => u.tierAfter === 'blaze' && u.tierBefore !== 'blaze') })
        if (f.unlocks.length) invalidate('achievements')
        setFresh(f)
      })
      .catch(() => { if (live) setFresh({ unlocks: [], claimed: true, near: [] }) })
    return () => { live = false }
  }, [])
  return fresh
}

/**
 * Chips in two rows at most, the last place taken by "+N more", which opens the rest: a player back
 * after a while can earn dozens at once. Only the chips that fit are drawn until then.
 */
function TwoRows({ children }: { children: ReactElement[] }) {
  const box = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [width, setWidth] = useState(0)
  // More than two rows ever hold at the widths the page takes; trimmed below to what fits.
  const start = Math.min(children.length, 12)
  const [fit, setFit] = useState(start)
  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const watch = new ResizeObserver(([e]) => setWidth(Math.round(e.contentRect.width)))
    watch.observe(el)
    return () => watch.disconnect()
  }, [])
  useLayoutEffect(() => setFit(start), [width, start])
  // Drop a chip until the last thing drawn (the "+N more", or the last chip) sits in the second row.
  useLayoutEffect(() => {
    const el = box.current
    if (!el || open || fit <= 1) return
    const tops = [...el.children].map((c) => Math.round(c.getBoundingClientRect().top))
    const rows = [...new Set(tops)].sort((a, b) => a - b)
    if (rows.length > 2 && tops[tops.length - 1] > rows[1]) setFit(fit - 1)
  })
  const hidden = open ? 0 : children.length - fit
  return (
    <div className="chips" ref={box}>
      {hidden > 0 ? children.slice(0, fit) : children}
      {hidden > 0 && <button type="button" className="more" onClick={() => setOpen(true)}>+{hidden} more</button>}
      {open && children.length > start && <button type="button" className="more" onClick={() => setOpen(false)}>Show less</button>}
    </div>
  )
}

/** Under Share once the reveal has closed: today's unlocks as chips (unclaimed for a guest), or what's within reach. */
function AchievementStrip({ fresh }: { fresh: FreshAchievements }) {
  const { unlocks, claimed, near } = fresh
  if (unlocks.length) {
    // One chip per category, at the tier today left it.
    const byCategory = new Map<BadgeCategory, (typeof unlocks)[number]>()
    for (const u of unlocks) byCategory.set(u.category, u)
    return (
      <section className={`ach-strip${claimed ? '' : ' guest'}`} aria-label="Achievements earned today">
        <div className="top">
          {claimed
            ? <span className="eyebrow"><b>{unlocks.length}</b> earned today</span>
            : <span className="eyebrow"><b>{unlocks.length}</b> unclaimed today</span>}
          {claimed ? <Link to="/you/achievements">See all ›</Link> : <Link to="/signin">Sign in to keep them ›</Link>}
        </div>
        <TwoRows>
          {unlocks.map((u) => (
            <span key={u.id}><Badge category={u.category} tier={byCategory.get(u.category)!.tierAfter ?? 'locked'} lit={u.category === 'phoenix' ? byCategory.get(u.category)!.count / u.total : undefined} size={20} />{u.name}</span>
          ))}
        </TwoRows>
      </section>
    )
  }
  if (!near.length) return null
  return (
    <section className="ach-strip" aria-label="Achievements within reach">
      <div className="top">
        <span className="eyebrow">Within reach</span>
        <Link to="/you/achievements">All achievements ›</Link>
      </div>
      <div className="items">
        {near.map((n) => (
          <div className="it" key={n.id}>
            <Badge category={n.category} tier="locked" size={20} />
            <span><b>{n.name}</b> <span className="faint small">· {n.left}</span></span>
            <span className="bar" aria-hidden="true"><i style={{ width: `${Math.round((n.have / n.need) * 100)}%` }} /></span>
          </div>
        ))}
      </div>
    </section>
  )
}
